import {assert} from 'console';
import {noop} from 'jquery';
import React, {useMemo} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {ColumnConstraint, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import {ProcedureColumnRowMetadata} from 'studies/search/ProcedureColumnUtils';
import {CLINICAL_STUDIES_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {COLORS} from 'ui-library/ColorPickerConstants';
import {Optional} from 'util/Types';

import {useStudyDesignItemTableSharedProps} from '../utils/StudyDesignItemUtil';
import {usePublishedProcedureFlowchartVersionIdsByProcedureId} from './PublishedProcedureFlowchartVersionUtils';
import {SampleDefinition} from './SampleDefinitionTable';

interface SampleDefinitionRow {
  label: string;
}

export interface SampleTestPlan {
  id?: string;
  sampleDefinition: SampleDefinitionRow | null;
  assayId: string | null;
  publishedAssayVersionId: string | null;
  isReadyForExecution: boolean;

  // Hydrated data
  // (these initialize as null during page load and then are set during component initialization)
  rowIndex: number | null;
  validationState: SampleTestPlanValidationState | null;
}

interface SampleTestPlanRow {
  sampleDefinition: SampleDefinitionRow | null;
  assays: Array<SampleTestPlanAssay>;
  rowIndex: number;
}

interface SampleTestPlanAssay {
  assayId: string | null;
  isReadyForExecution: boolean;
  validationState: SampleTestPlanValidationState | null;
  publishedAssayVersionId: string | null;
}

type PersistedSampleTestPlan = SampleTestPlan & {
  id: string;
  sampleDefinition: SampleDefinitionRow;
  assayId: string;
};

export const INACTIVE_SAMPLE_TEST_PLAN_TOOLTIP = 'Proceed to execution to finalize this sample testing plan';

enum SAMPLE_TEST_PLAN_COLUMNS {
  SAMPLE_DEFINITION = 0,
  ASSAY = 1,
}
interface SampleTestPlanColumnData {
  name: string;
  width: number;
}
const COLUMN_DATA: Array<SampleTestPlanColumnData> = [];
COLUMN_DATA[SAMPLE_TEST_PLAN_COLUMNS.SAMPLE_DEFINITION] = {
  name: 'Sample definition',
  width: 200,
};
COLUMN_DATA[SAMPLE_TEST_PLAN_COLUMNS.ASSAY] = {
  name: 'Assay',
  width: 600,
};

/**
 * Any truthy value in the validation state is a blocking validation error for saving the study design.
 */
export interface SampleTestPlanValidationState {
  isMissingSampleDefinition: boolean;
  isMissingAssay: boolean;
  isInvalidSampleDefinition: boolean;
  isDuplicateSampleDefinitionRow: boolean;
  isDuplicateTestPlan: boolean;
}

function getSampleTestPlanAssay(
  sampleTestPlan: SampleTestPlan,
  publishedFlowchartVersionIdsByProcedureId: Record<string, string>
): SampleTestPlanAssay {
  return {
    assayId: sampleTestPlan.assayId ?? null,
    validationState: sampleTestPlan.validationState,
    isReadyForExecution: sampleTestPlan.isReadyForExecution,
    publishedAssayVersionId: sampleTestPlan.assayId
      ? (publishedFlowchartVersionIdsByProcedureId[sampleTestPlan.assayId] ?? null)
      : null,
  };
}

function buildSampleTestPlanRows(
  sampleTestPlans: Array<SampleTestPlan>,
  publishedFlowchartVersionIdsByProcedureId: Record<string, string>
): Array<SampleTestPlanRow> {
  const sampleTestPlanRows: Array<SampleTestPlanRow> = [];
  for (const sampleTestPlan of sampleTestPlans) {
    let existingRow: SampleTestPlanRow | null = null;
    if (sampleTestPlan.rowIndex != null) {
      existingRow = sampleTestPlanRows.find((row) => row.rowIndex === sampleTestPlan.rowIndex) ?? null;
    } else if (sampleTestPlan.sampleDefinition) {
      existingRow =
        sampleTestPlanRows.find(
          (row) => row.sampleDefinition?.label === sampleTestPlan.sampleDefinition?.label
        ) ?? null;
    }

    if (existingRow) {
      existingRow.assays.push(
        getSampleTestPlanAssay(sampleTestPlan, publishedFlowchartVersionIdsByProcedureId)
      );
    } else {
      sampleTestPlanRows.push({
        sampleDefinition: sampleTestPlan.sampleDefinition,
        assays: [getSampleTestPlanAssay(sampleTestPlan, publishedFlowchartVersionIdsByProcedureId)],
        rowIndex: sampleTestPlan.rowIndex ?? sampleTestPlanRows.length,
      });
    }
  }
  return sampleTestPlanRows.sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));
}

/**
 *  Update data when any individual cell is updated.
 */
export function updateTableCells(
  table: Table,
  setSampleTestPlans: (sampleTestPlans: Array<SampleTestPlan>) => void,
  persistedSampleTestPlans: Array<PersistedSampleTestPlan>,
  sampleDefinitions: Array<SampleDefinition>
): void {
  const remainingSampleTestPlans: Array<PersistedSampleTestPlan> = [...persistedSampleTestPlans];

  // Convert multi-assay rows into individual sample test plans
  let newSampleTestPlans: Array<SampleTestPlan> = [];
  table.table.forEach((tableRow, rowIndex) => {
    const sampleDefinitionCell = tableRow[SAMPLE_TEST_PLAN_COLUMNS.SAMPLE_DEFINITION];
    const sampleDefinition = sampleDefinitionCell?.text ? {label: sampleDefinitionCell?.text} : null;

    const assayCell = tableRow[SAMPLE_TEST_PLAN_COLUMNS.ASSAY];
    const assayColumnTypedValue = assayCell.typedValue;
    const assays =
      assayColumnTypedValue?.type === BaseTypedValueType.PROCEDURE ? assayColumnTypedValue.procedures : [];

    if (assays.length === 0) {
      newSampleTestPlans.push({
        sampleDefinition,
        assayId: null,
        publishedAssayVersionId: null,
        isReadyForExecution: false,
        rowIndex,
        validationState: null,
      });
    } else {
      for (const assay of assays) {
        // Is this sample test plan already saved in the study design?
        const persistedIdx = remainingSampleTestPlans.findIndex(
          (sampleTestPlan) =>
            sampleDefinition &&
            sampleTestPlan.sampleDefinition.label.toLowerCase() === sampleDefinition.label.toLowerCase() &&
            sampleTestPlan.assayId === assay.id
        );
        // If so, record it with the same ID and remove it from the remaining sample test plans (to avoid duplicates)
        if (persistedIdx > -1) {
          const [persistedSampleTestPlan] = remainingSampleTestPlans.splice(persistedIdx, 1);
          newSampleTestPlans.push({
            id: persistedSampleTestPlan.id,
            sampleDefinition,
            assayId: assay.id,
            publishedAssayVersionId: persistedSampleTestPlan.publishedAssayVersionId,
            isReadyForExecution: persistedSampleTestPlan.isReadyForExecution,
            rowIndex,
            validationState: null,
          });
        }
        // Otherwise, record it as a new sample test plan with no persisted id
        else {
          newSampleTestPlans.push({
            sampleDefinition,
            assayId: assay.id,
            publishedAssayVersionId: null,
            isReadyForExecution: false,
            rowIndex,
            validationState: null,
          });
        }
      }
    }
  });

  // Restore any persisted sample test plans with isReadyForExecution=true that were not found in the table
  // This could occur if the user typed backspace or cut-and-pasted into a cell
  const missingSampleTestPlans = remainingSampleTestPlans.filter(
    (sampleTestPlan) => sampleTestPlan.isReadyForExecution
  );

  if (missingSampleTestPlans.length > 0) {
    notifications.warn(
      "Some assays couldn't be removed because they've already been approved for this study design."
    );
  }

  for (const missingSampleTestPlan of missingSampleTestPlans) {
    // Find the row index that matches the missing sample test plan
    const firstMatchingTestPlan = newSampleTestPlans.find(
      (sampleTestPlan) =>
        sampleTestPlan.sampleDefinition?.label.toLowerCase() ===
        missingSampleTestPlan.sampleDefinition.label.toLowerCase()
    );
    const rowIndex = firstMatchingTestPlan ? firstMatchingTestPlan.rowIndex : null;
    newSampleTestPlans.push({
      ...missingSampleTestPlan,
      rowIndex,
    });
  }

  // Remove any redundant sample test plans now that those plans have been restored
  // Only keep test plans where: the assay is defined OR the sample definition is not in the missing sample test plans
  newSampleTestPlans = newSampleTestPlans.filter(
    (sampleTestPlan) =>
      sampleTestPlan.assayId != null ||
      !missingSampleTestPlans.some(
        (missingSampleTestPlan) =>
          missingSampleTestPlan.sampleDefinition.label.toLowerCase() ===
          sampleTestPlan.sampleDefinition?.label.toLowerCase()
      )
  );

  // Update the sample test plans in the study design
  setSampleTestPlans(
    newSampleTestPlans
      .map((sampleTestPlan) => ({
        ...sampleTestPlan,
        validationState: {
          isMissingSampleDefinition: sampleTestPlan.sampleDefinition == null,
          isMissingAssay: sampleTestPlan.assayId == null,
          isInvalidSampleDefinition: isInvalidSampleDefinition(sampleTestPlan, sampleDefinitions),
          isDuplicateSampleDefinitionRow: hasDuplicateSampleDefinitionRow(sampleTestPlan, newSampleTestPlans),
          isDuplicateTestPlan: hasDuplicateTestPlans(sampleTestPlan, newSampleTestPlans),
        },
      }))
      // Restore the rowIndex ordering of the sample test plans (for missing plans that were restored)
      .sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0))
  );
}

function isInvalidSampleDefinition(
  sampleTestPlan: SampleTestPlan,
  sampleDefinitions: Array<SampleDefinition>
): boolean {
  return (
    sampleTestPlan.sampleDefinition != null &&
    sampleDefinitions.filter(
      (sampleDefinition) =>
        sampleDefinition.label.toLowerCase() === sampleTestPlan.sampleDefinition?.label.toLowerCase()
    ).length === 0
  );
}

function hasDuplicateSampleDefinitionRow(
  sampleTestPlan: SampleTestPlan,
  sampleTestPlans: Array<SampleTestPlan>
): boolean {
  const sampleDefinitionName = sampleTestPlan.sampleDefinition?.label.toLowerCase();
  const rowIndex = sampleTestPlan.rowIndex;
  return (
    sampleTestPlan.sampleDefinition != null &&
    sampleTestPlan.rowIndex != null &&
    sampleTestPlans.filter(
      (sampleTestPlan) =>
        sampleTestPlan.sampleDefinition?.label.toLowerCase() === sampleDefinitionName &&
        sampleTestPlan.rowIndex != null &&
        sampleTestPlan.rowIndex !== rowIndex
    ).length > 0
  );
}

function hasDuplicateTestPlans(
  sampleTestPlan: SampleTestPlan,
  sampleTestPlans: Array<SampleTestPlan>
): boolean {
  const sampleDefinitionName = sampleTestPlan.sampleDefinition?.label.toLowerCase();
  const assayId = sampleTestPlan.assayId;
  return (
    sampleTestPlan.sampleDefinition != null &&
    sampleTestPlan.assayId != null &&
    sampleTestPlans.filter(
      (sampleTestPlan) =>
        sampleTestPlan.sampleDefinition?.label.toLowerCase() === sampleDefinitionName &&
        sampleTestPlan.assayId === assayId
    ).length > 1
  );
}

const getSampleDefinitionLocked = (sampleTestPlanRow: SampleTestPlanRow): boolean =>
  sampleTestPlanRow.assays.some((assay) => assay.isReadyForExecution);

function makeErrorTriangle(text: string, location: TriangleLocation): JSX.Element {
  return (
    <ErrorTriangle state={TriangleState.ILLEGAL} error={{type: '', message: text}} location={location} />
  );
}

const getSampleDefinitionErrorTriangle = (sampleTestPlanRow: SampleTestPlanRow): Optional<JSX.Element> => {
  if (sampleTestPlanRow.sampleDefinition == null) {
    return makeErrorTriangle('Sample definition is required', TriangleLocation.CELL);
  } else {
    return undefined;
  }
};

const getAssayErrorTriangle = (nonEmptyAssays: Array<SampleTestPlanAssay>): Optional<JSX.Element> => {
  if (nonEmptyAssays.length === 0) {
    return makeErrorTriangle('Assay is required', TriangleLocation.CELL);
  } else {
    return undefined;
  }
};

const getRowErrorTriangle = (sampleTestPlanRow: SampleTestPlanRow): Optional<JSX.Element> => {
  if (sampleTestPlanRow.assays.some((assay) => assay.validationState?.isDuplicateSampleDefinitionRow)) {
    return makeErrorTriangle('Sample Definition already in use', TriangleLocation.AGGREGATE);
  } else if (sampleTestPlanRow.assays.some((assay) => assay.validationState?.isDuplicateTestPlan)) {
    return makeErrorTriangle(
      'Sample Definition and Assay combination already in use',
      TriangleLocation.AGGREGATE
    );
  } else {
    return undefined;
  }
};

function makeBaseCell(): Cell {
  return {
    text: '',
    bold: false,
    align: 'left',
    backgroundColor: COLORS.WHITE,
  };
}

function makeInitialSampleTestPlanTable(
  tableTitle: string,
  sampleTestPlans: Array<SampleTestPlan>,
  sampleDefinitions: Array<SampleDefinition>,
  locked: boolean,
  publishedFlowchartVersionIdsByProcedureId: Record<string, string>
): Table {
  const sampleDefinitionOptions: Array<string> = sampleDefinitions.map(
    (sampleDefinition) => sampleDefinition.label
  );

  const sampleTestPlanRows = buildSampleTestPlanRows(
    sampleTestPlans,
    publishedFlowchartVersionIdsByProcedureId
  );

  const columnConstraints: Array<ColumnConstraint | null> = COLUMN_DATA.map((column, index) => {
    switch (index) {
      case SAMPLE_TEST_PLAN_COLUMNS.SAMPLE_DEFINITION:
        return {
          viewer: true,
          columnDefinition: {
            name: column.name,
            isRequired: true,
            type: TableColumnType.OPTION_SEARCH_SELECTOR,
            options: sampleDefinitionOptions,
            emptyOptionsDisplay: <div>No sample definitions have been defined</div>,
          },
        };
      case SAMPLE_TEST_PLAN_COLUMNS.ASSAY:
        return {
          viewer: true,
          columnDefinition: {
            name: column.name,
            isRequired: true,
            type: TableColumnType.PROCEDURE,
            procedureTypes: [ProcedureType.ASSAY],
            placeholder: 'Search for an assay',
            shouldAllowMultiple: true,
          },
        };
      default:
        assert(false, `Unexpected column index: ${index}`);
        return null;
    }
  });

  function makeSampleTestPlanCell(index: number, sampleTestPlanRow: SampleTestPlanRow): Cell {
    const nonEmptyAssays = sampleTestPlanRow.assays.filter((assay) => assay.assayId != null);
    switch (index) {
      case SAMPLE_TEST_PLAN_COLUMNS.SAMPLE_DEFINITION:
        return {
          ...makeBaseCell(),
          text: sampleTestPlanRow.sampleDefinition?.label ?? '',
          typedValue: sampleTestPlanRow.sampleDefinition?.label
            ? {type: BaseTypedValueType.STRING, rawValue: sampleTestPlanRow.sampleDefinition.label}
            : null,
          userAnnotationIndicator: getSampleDefinitionErrorTriangle(sampleTestPlanRow),
          locked: getSampleDefinitionLocked(sampleTestPlanRow),
        };
      case SAMPLE_TEST_PLAN_COLUMNS.ASSAY:
        return {
          ...makeBaseCell(),
          text: '',
          typedValue:
            nonEmptyAssays.length > 0
              ? {
                  type: BaseTypedValueType.PROCEDURE,
                  procedures: nonEmptyAssays.map((assay) => ({
                    id: assay.assayId ?? '',
                    name: '',
                    flowchartVersionId: assay.publishedAssayVersionId ?? undefined,
                  })),
                }
              : null,
          userAnnotationIndicator: getAssayErrorTriangle(nonEmptyAssays),
          locked,
        };
      default:
        assert(false, `Unexpected column index: ${index}`);
        return makeBaseCell();
    }
  }

  return {
    name: tableTitle,
    widths: COLUMN_DATA.map((column) => column.width),
    columnLabels: COLUMN_DATA.map((column) => column.name),
    columnConstraints,
    headerCol: true,
    headerRow: false,
    table: sampleTestPlanRows.map((sampleTestPlanRow) =>
      COLUMN_DATA.map((_, index) => makeSampleTestPlanCell(index, sampleTestPlanRow))
    ),
    rowData: sampleTestPlanRows.map((sampleTestPlanRow) => {
      const userAnnotationIndicator = getRowErrorTriangle(sampleTestPlanRow);
      const rowMetadata: ProcedureColumnRowMetadata = {
        procedureColumns: [
          {
            columnGroup: null,
            readOnlyIds: sampleTestPlanRow.assays
              .filter((assay) => assay.isReadyForExecution && assay.assayId != null)
              .map((assay) => assay.assayId as string),
          },
        ],
      };
      return {
        uiData: {
          userAnnotationIndicator,
          // If there is a row level error and the sample test plan is not ready for execution,
          // we want to avoid showing two tooltips that conflict with each other. In this case,
          // the error tooltip takes precedence, and we don't show the unsubmitted changes tooltip.
          hasUnsubmittedChanges:
            userAnnotationIndicator === undefined &&
            sampleTestPlanRow.assays.some((assay) => !assay.isReadyForExecution),
          unsubmittedChangesTooltip: INACTIVE_SAMPLE_TEST_PLAN_TOOLTIP,
        },
        rowMetadata,
        isFinalized: sampleTestPlanRow.assays.some((assay) => assay.isReadyForExecution),
      };
    }),
    lockedDims: {
      allowAddRows: !locked,
      allowRemoveRows: !locked,
      allowEditCols: false,
    },
    _hideRename: true,
    allowAddRows: !locked,
    allowRowRemoval: !locked,
    allowColumnEdits: false,
    allowNoRows: false,
    maxRows: CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_SAMPLE_TEST_PLANS_PER_DESIGN,
  };
}

export function SampleTestPlanTable(props: {
  tableTitle: string;
  sampleTestPlans: Array<SampleTestPlan>;
  sampleDefinitions: Array<SampleDefinition>;
  setSampleTestPlans: (sampleTestPlans: Array<SampleTestPlan>) => void;
  locked: boolean;
  viewer: boolean;
}): JSX.Element {
  const sharedProps = useStudyDesignItemTableSharedProps();

  const sampleDefinitionOptions = useMemo(
    () => props.sampleDefinitions.filter((sampleDefinition) => sampleDefinition.label !== ''),
    [props.sampleDefinitions]
  );

  const persistedSampleTestPlans = useMemo(
    () =>
      props.sampleTestPlans.filter(
        (sampleTestPlan): sampleTestPlan is PersistedSampleTestPlan => sampleTestPlan.id != null
      ),
    [props.sampleTestPlans]
  );

  const flowchartVersionIdsByProcedureId = usePublishedProcedureFlowchartVersionIdsByProcedureId(
    persistedSampleTestPlans.map((sampleTestPlan) => ({
      procedureId: sampleTestPlan.assayId,
      publishedProcedureFlowchartVersionId: sampleTestPlan.publishedAssayVersionId,
    }))
  );

  const table = makeInitialSampleTestPlanTable(
    props.tableTitle,
    props.sampleTestPlans,
    sampleDefinitionOptions,
    props.locked,
    flowchartVersionIdsByProcedureId
  );
  return (
    <div className="u-flex-display mediocre">
      <div className="study-design-export--max-width">
        <SimpleTableEditable
          {...standaloneProps}
          {...sharedProps}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          isLeftAlignRowHeader={false}
          locked={props.locked}
          viewer={props.viewer}
          table={table}
          cid="sample-testing-plan-tables"
          updateHandler={(_, {table}: {table: Table}) => {
            updateTableCells(
              table,
              props.setSampleTestPlans,
              persistedSampleTestPlans,
              sampleDefinitionOptions
            );
          }}
          storeState={noop}
          restoreState={noop}
          isCollapsible={true}
          hideRowCount={true}
          allowReorderDimensions={{
            [TableDimension.ROW]: true,
            [TableDimension.COLUMN]: false,
          }}
          dataTestAttrs={{component: 'SampleTestPlanTable', element: 'simpleTableEditable'}}
        />
      </div>
    </div>
  );
}
